import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { ListComponentProps } from '../../Routers/CrudRouter';
import { useTableColumnsMemo } from './tableColumns';
import { tryCatchJsonByAction } from '../../../utils/fetchUtils';
import { TableButtons } from '../../../components/Table/TableButtons';
import { Table } from '../../../components/Table/Table';
import { ArticleResponse, ArticleType, PutujmoSite, SimplePropertyModel, UpdateArticleOrdersRequest, UpdateSingleArticleOrderRequest } from '../../../swagger/putujmoAdminService';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { createArticleAction, deleteArticleAction, getAllArticlesAction, getArticleAction, updateArticleOrdersAction } from '../../../features/articles/action';
import { MultiSelect } from '../../../components/MultiSelect/MultiSelect';
import { LanguageEnum, languageOptions, siteOptions } from '../../../utils/languageUtils';
import { Button } from '../../../components/Button/Button';
import { getAllSimplePropertiesAction } from '../../PhobsProperties/action';
import { getSimplePropertyDisplayName } from '../../PhobsProperties/displayUtils';

export const ArticleTable = ({dataChangedTopic, publishDataChanged, additionalData}: ListComponentProps) => {
	const [putujmoSite, setPutujmoSite] = useState(PutujmoSite.Serbia);
	const [language, setLanguage] = useState(LanguageEnum.Serbian);
	const [selectedId, setSelectedId] = useState<string | number | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<ArticleResponse[]>([]);
	const [updatedData, setUpdatedData] = useState<ArticleResponse[]>([]);
	const [simpleProperties, setSimpleProperties] = useState<SimplePropertyModel[]>([]);

	const phobsNameByPropertyId = useMemo(() => {
		const map = new Map<string, string>();
		for (const property of simpleProperties) {
			map.set(property.propertyId, getSimplePropertyDisplayName(property));
		}
		return map;
	}, [simpleProperties]);

	const columns = useTableColumnsMemo(additionalData.articleType, phobsNameByPropertyId);

	const fetchDataCallback = useCallback(
		async () => {
			setLoading(true);
			const bindedAction = getAllArticlesAction.bind(null, putujmoSite, additionalData.articleType, language)
			var getResponse = await tryCatchJsonByAction(bindedAction);
			if (getResponse.success) {
				const items = getResponse.response || [];
				setData(items);
			}
			setLoading(false);
		},
		[putujmoSite, language, additionalData]
	)

	useEffect(
		() => {
			fetchDataCallback();
		},
		[fetchDataCallback]
	)

	useEffect(
		() => {
			const subscription = dataChangedTopic?.subscribe(fetchDataCallback);
			return () => {
				subscription?.unsubscribe();
			}
		},
		[fetchDataCallback, dataChangedTopic]
	)

	useEffect(
		() => {
			if (additionalData.articleType !== ArticleType.PPSCard) {
				setSimpleProperties([]);
				return;
			}
			let cancelled = false;
			(async () => {
				try {
					const res = await getAllSimplePropertiesAction(putujmoSite, language);
					if (!cancelled) setSimpleProperties(res || []);
				} catch {
					if (!cancelled) setSimpleProperties([]);
				}
			})();
			return () => {
				cancelled = true;
			};
		},
		[putujmoSite, language, additionalData.articleType]
	)

	const deleteCallback = useCallback(
		async (id: number) => {
			const bindedAction = deleteArticleAction.bind(null, id);
			const response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				toast.success('Element je uspešno obrisan');
				setSelectedId(undefined);
				publishDataChanged();
			}
		},
		[publishDataChanged]
	)

	const copyToHistorical = useCallback(
		async () => {
			setLoading(true);
			if (selectedId &&  typeof selectedId === 'number') {
				const bindedAction = getArticleAction.bind(null, selectedId);
				var getResponse = await tryCatchJsonByAction(bindedAction);
				if (getResponse.success) {
					const article: ArticleResponse = getResponse.response || new ArticleResponse();
					article.articleType = ArticleType.HistoricalHeritage;
					const bindedAction = createArticleAction.bind(null, article);
					const response = await tryCatchJsonByAction(bindedAction);
					setLoading(false);
		
					if (response.success) {
						toast.success('Novi element je uspešno kreiran.');
						publishDataChanged();
					} 
				}
			}

			setLoading(false);
		},
		[publishDataChanged, selectedId]
	)

	const saveOrderCallback = useCallback(
		async () => {
			setLoading(true);
			const model = new UpdateArticleOrdersRequest({
				orders: updatedData.map((item, index) => new UpdateSingleArticleOrderRequest({
					articleId: item.id,
					order: index + 1
				}))
			});
			const bindedAction = updateArticleOrdersAction.bind(null, model);
			var response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				toast.success('Redosled je uspešno sačuvan.');
				publishDataChanged();
			}

			setLoading(false);
		},
		[publishDataChanged, updatedData]
	)

	return (
		<div>
			<div className='flex-start-container'>
				<div className='m5'>
					<MultiSelect
						value={[putujmoSite]}
						items={siteOptions}
						onChange={(value) => {
							setPutujmoSite(value.length > 0 ? value[0] : PutujmoSite.Serbia)
						}}
						getItemId={(item: any) => item.id}
						getItemText={(item: any) => item.text}
					/>
				</div>
				<div className='m5'>
					<MultiSelect
						value={[language]}
						items={languageOptions}
						onChange={(value) => {
							setLanguage(value.length > 0 ? value[0] : LanguageEnum.Serbian)
						}}
						getItemId={(item: any) => item.id}
						getItemText={(item: any) => item.text}
					/>
				</div>
			</div>
			<TableButtons
				selectedId={selectedId}
				onDelete={deleteCallback}
				isViewable
				AdditionalButton={(
					<>
						{additionalData.copyToHistorical && (
							<Button
								onClick={copyToHistorical}
								value='Kopiraj u nasleđe'
								disabled={!selectedId}
							/>
						)}
						<Button
							onClick={saveOrderCallback}
							value='Sačuvaj redosled'
						/>
					</>
				)}
			/>
			<Table
				columns={columns}
				data={data}
				onSelectedChange={setSelectedId}
				onSortChange={setUpdatedData}
				isReorder
			/>
			{loading && <LoadingSpinner />}
		</div>
	)
}
